import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { Button } from '@/shared/components';
import { handleUserMutationError } from '@/features/users/services/userErrorHandler';
import { useDealerBankAccountMutations, useDealerBankAccounts } from '../hooks/useDealerBankAccounts';
import type { DealerBankAccountFormInput } from '../schemas/dealerBankAccount.schema';
import { DealerBankAccountDataTable } from './DealerBankAccountDataTable';
import { DealerBankAccountForm } from './DealerBankAccountForm';

interface DealerBankAccountsProps {
  dealerId: string;
}

export function DealerBankAccounts({ dealerId }: DealerBankAccountsProps) {
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading } = useDealerBankAccounts(dealerId);
  const { createMutation, deleteMutation } = useDealerBankAccountMutations(dealerId);

  const handleCreate = async (payload: DealerBankAccountFormInput) => {
    try {
      await createMutation.mutateAsync({
        account_name: payload.account_name,
        account_number: payload.account_number,
        sort_code: payload.sort_code,
        bank_name: payload.bank_name || undefined,
        is_primary: payload.is_primary,
      });
      setShowForm(false);
    } catch (error) {
      handleUserMutationError(error);
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onError: (error) => handleUserMutationError(error),
    });
  };

  return (
    <div className="space-y-6">
      <DealerBankAccountDataTable
        data={data ?? []}
        isLoading={isLoading}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
        emptyMessage="No bank accounts added for this dealer yet."
      />

      <div className="border-t border-mp-border pt-6 max-w-2xl">
        {showForm ? (
          <DealerBankAccountForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isLoading={createMutation.isPending}
          />
        ) : (
          <div>
            <h3 className="font-semibold mb-2">Add Bank Account</h3>
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Register a new bank account for this dealer. The first account added becomes the primary by default.
              </p>
              <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setShowForm(true)}
                  className="whitespace-nowrap"
                >
                  Add Bank Account
                </Button>
              </PermissionGuard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
